import React, { useState } from "react";
import { API, authFetch } from "@/App";
import { toast } from "sonner";
import { Loader2, Zap, Mail, X, FileText, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";

export function EnrichOrderModal({ isOpen, onClose, orderId, orderName, onExtracted }) {
  const [emailContent, setEmailContent] = useState("");
  const [isExtracting, setIsExtracting] = useState(false);
  const [isUploadingPdf, setIsUploadingPdf] = useState(false);

  const handleExtract = async () => {
    if (!emailContent.trim()) {
      toast.error("Veuillez coller le contenu de l'email");
      return;
    }
    setIsExtracting(true);
    try {
      const response = await authFetch(`${API}/orders/extract`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email_content: emailContent }),
      });
      if (response.ok) {
        const data = await response.json();
        onExtracted(data.extracted_data, emailContent);
        setEmailContent("");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Erreur lors de l'extraction");
      }
    } catch (error) {
      console.error("Extraction failed:", error);
      toast.error("Erreur lors de l'extraction");
    } finally {
      setIsExtracting(false);
    }
  };

  const handlePdfUpload = async (file) => {
    if (!file || !file.name.toLowerCase().endsWith('.pdf')) {
      toast.error("Veuillez sélectionner un fichier PDF");
      return;
    }
    setIsUploadingPdf(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await authFetch(`${API}/pdf/extract-text`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        if (data.text?.trim()) {
          setEmailContent(data.text.trim());
          toast.success(`Texte extrait de "${file.name}"`);
        } else {
          toast.error("Aucun texte trouvé dans le PDF");
        }
      } else {
        toast.error("Erreur lors de l'extraction du PDF");
      }
    } catch {
      toast.error("Erreur lors du chargement du PDF");
    } finally {
      setIsUploadingPdf(false);
    }
  };

  const handleClose = () => {
    setEmailContent("");
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-['Manrope']">
            <Mail className="w-5 h-5 text-orange-500" />
            Enrichir : {orderName}
          </DialogTitle>
          <DialogDescription>
            Collez un email (commande, paiement, confirmation...) pour compléter ou mettre à jour les données de cette ligne.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-auto space-y-3 py-2">
          {/* PDF upload zone */}
          <div
            className="p-3 border-2 border-dashed border-zinc-300 rounded-lg hover:border-orange-400 hover:bg-zinc-50 transition-all cursor-pointer"
            onClick={() => {
              const input = document.createElement('input');
              input.type = 'file';
              input.accept = '.pdf';
              input.onchange = (e) => handlePdfUpload(e.target.files[0]);
              input.click();
            }}
            data-testid="enrich-pdf-zone"
          >
            <div className="flex items-center justify-center gap-2 text-sm">
              {isUploadingPdf ? (
                <><Loader2 className="w-4 h-4 animate-spin text-orange-500" /><span className="text-orange-600">Extraction du PDF...</span></>
              ) : (
                <><FileText className="w-4 h-4 text-zinc-400" /><span className="text-zinc-500"><span className="text-orange-500 font-medium">Importer un PDF</span> ou coller le texte ci-dessous</span><Upload className="w-3.5 h-3.5 text-zinc-400" /></>
              )}
            </div>
          </div>

          <Textarea
            value={emailContent}
            onChange={(e) => setEmailContent(e.target.value)}
            placeholder="Collez ici le contenu de l'email de commande, paiement ou confirmation..."
            className="min-h-[280px] font-mono text-sm resize-y"
            data-testid="enrich-email-textarea"
          />
        </div>

        <DialogFooter className="flex-shrink-0">
          <Button variant="outline" onClick={handleClose}>Annuler</Button>
          <Button
            onClick={handleExtract}
            disabled={isExtracting || !emailContent.trim()}
            className="bg-orange-500 hover:bg-orange-600 text-white gap-2"
            data-testid="enrich-extract-btn"
          >
            {isExtracting ? (
              <><Loader2 className="w-4 h-4 animate-spin" />Extraction...</>
            ) : (
              <><Zap className="w-4 h-4" />Extraire &amp; Comparer</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
